export const ALL_STATES: string[] = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois',
  'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts',
  'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota',
  'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina',
  'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming',
];

export interface ChoiceOption {
  value: string;
  label: string;
  description?: string;
}

export const ENTITY_OPTIONS: ChoiceOption[] = [
  { value: '501c3', label: '501(c)(3) — Charitable', description: 'Churches, schools, hospitals, charities, educational orgs' },
  { value: '501c4', label: '501(c)(4) — Social Welfare', description: 'Civic leagues and community organizations' },
  { value: '501c5', label: '501(c)(5) — Labor or Agricultural', description: 'Labor unions, farm bureaus' },
  { value: '501c6', label: '501(c)(6) — Trade or Business Association', description: 'Chambers of commerce, trade groups' },
  { value: '501c7', label: '501(c)(7) — Social or Recreation Club', description: 'Country clubs, hobby groups' },
  { value: '501c8', label: '501(c)(8) — Fraternal Beneficiary Society', description: 'Fraternal lodges with member benefits' },
  { value: '501c9', label: '501(c)(9) — Employee Benefit Association', description: 'VEBAs' },
  { value: '501c10', label: '501(c)(10) — Domestic Fraternal Society', description: 'Lodge-system societies without insurance' },
  { value: '501c19', label: '501(c)(19) — Veterans Organization', description: 'Posts and auxiliaries for veterans' },
];

export const REVENUE_OPTIONS: ChoiceOption[] = [
  { value: 'under50k', label: 'Less than $50,000', description: 'Files Form 990-N (e-Postcard)' },
  { value: '50to200k', label: 'Between $50,000 and $200,000', description: 'May qualify for Form 990-EZ' },
  { value: 'over200k', label: 'More than $200,000', description: 'Files the full Form 990' },
];

export const ASSETS_OPTIONS: ChoiceOption[] = [
  { value: 'under500k', label: 'Less than $500,000' },
  { value: 'over500k', label: '$500,000 or more', description: 'Full Form 990 may be required even if revenue is lower' },
];

export const FUNDRAISING_METHOD_OPTIONS: ChoiceOption[] = [
  { value: 'individual', label: 'Gifts from Individual Donors' },
  { value: 'online', label: 'Online Donations' },
  { value: 'grants', label: 'Grants from Foundations' },
  { value: 'events', label: 'Fundraising Events' },
  { value: 'mail', label: 'Letters or Mailings' },
  { value: 'phone', label: 'Phone Calls' },
  { value: 'major', label: 'Major Donor Gifts' },
  { value: 'corporate', label: 'Corporate Sponsorships' },
  { value: 'planned', label: 'Planned Giving' },
];

export const EMPLOYEE_OPTIONS: ChoiceOption[] = [
  { value: 'yes', label: 'W-2 Employees', description: 'People on your payroll who receive a W-2' },
  { value: 'contractors', label: 'Independent Contractors (1099)', description: 'People paid on a 1099 basis' },
  { value: 'no', label: 'All Volunteer — No Paid Staff', description: 'Nobody at the organization is paid' },
];

export const YES_NO_OPTIONS: ChoiceOption[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

export interface AssessmentResponses {
  states: string[];
  fundraisingStates: string[];
  entity: string;
  isChurch: 'yes' | 'no' | null;
  revenue: string;
  assets: string;
  fundraising: string[];
  usesProfessionalFundraiser: 'yes' | 'no' | '';
  conductRaffles: 'yes' | 'no' | '';
  hasEmployees: string[];
}

export const INITIAL_RESPONSES: AssessmentResponses = {
  states: [],
  fundraisingStates: [],
  entity: '',
  isChurch: null,
  revenue: '',
  assets: '',
  fundraising: [],
  usesProfessionalFundraiser: '',
  conductRaffles: '',
  hasEmployees: [],
};

export type StepId =
  | 'states'
  | 'fundraisingStates'
  | 'entity'
  | 'revenue'
  | 'assets'
  | 'fundraising'
  | 'usesProfessionalFundraiser'
  | 'conductRaffles'
  | 'hasEmployees';

export interface StepConfig {
  id: StepId;
  shortLabel: string;
  title: string;
  subtitle: string;
}

export const STEPS: StepConfig[] = [
  {
    id: 'states',
    shortLabel: 'Location',
    title: 'Where is the organization based?',
    subtitle: 'Select every state where the organization is officially incorporated, registered, or has a physical office.',
  },
  {
    id: 'fundraisingStates',
    shortLabel: 'Fundraising states',
    title: 'Where does the organization solicit donations?',
    subtitle: 'Include every state where they fundraise — online, by mail, by phone, at events, or in person.',
  },
  {
    id: 'entity',
    shortLabel: 'Entity type',
    title: 'What kind of nonprofit is it?',
    subtitle: 'Pick the IRS tax-exempt classification that matches the organization.',
  },
  {
    id: 'revenue',
    shortLabel: 'Revenue',
    title: 'Annual gross revenue?',
    subtitle: 'Total annual gross revenue (before expenses) — drives the IRS form requirement.',
  },
  {
    id: 'assets',
    shortLabel: 'Assets',
    title: 'Total assets?',
    subtitle: 'Cash, investments, property, and equipment at the end of the fiscal year.',
  },
  {
    id: 'fundraising',
    shortLabel: 'Fundraising methods',
    title: 'How does the organization raise money?',
    subtitle: 'Check all that apply. Drives state-specific solicitation rules.',
  },
  {
    id: 'usesProfessionalFundraiser',
    shortLabel: 'Pro fundraiser',
    title: 'Pays an outside professional fundraiser?',
    subtitle: 'A paid third-party company or individual — not staff or volunteers.',
  },
  {
    id: 'conductRaffles',
    shortLabel: 'Raffles / gaming',
    title: 'Conducts raffles, bingo, or other gaming?',
    subtitle: 'Many states require permits or restrict charitable gaming.',
  },
  {
    id: 'hasEmployees',
    shortLabel: 'Workers',
    title: 'What types of paid workers?',
    subtitle: 'Select all that apply. Drives payroll/employment compliance.',
  },
];
